import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import cx from 'classnames';
import {useIsMediumAndUp, useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {Button} from '~library/atoms/Button';
import {EditLineIcon} from '~library/svg/v2/EditLineIcon';
import {PlusIcon} from '~library/svg/v2/PlusIcon';
import {Typography} from '~library/atoms/Typography';
import {AdaptiveModal} from '~library/organisms/AdaptiveModal';
import {isHost} from '~/ViewInvitation/utils';
import {useAllPhotos} from '~/ViewInvitation/hooks/usePhotos';
import {useImageProxySizer} from '~/ViewInvitation/hooks/useImageProxySizer';
import {PhotoViewer} from '~/ViewInvitation/routes/Photos/PhotoViewer/PhotoViewer';
import {PhotoUploadButton} from '~/ViewInvitation/routes/Landing/PhotoUploadButton';

import 'react-loading-skeleton/dist/skeleton.css';
import './PhotoGallery.sass';

export const PHOTO_GALLERY_PAGE_CACHE_SIZE = 30;

const EmptyState = ({handlePost, fileUploadRef}) => {
  const eventTitle = useSelector((state) => state.eventDetails?.title);

  return (
    <div className="photo-gallery__empty-state__container">
      <Typography variant="header5">{eventTitle}</Typography>
      <Typography variant="paragraph2">No photos yet - be the first to share!</Typography>
      <PhotoUploadButton largeButton handlePost={handlePost} fileUploadRef={fileUploadRef}>
        Add photos
      </PhotoUploadButton>
    </div>
  );
};

const loadingSkeletons = (count) =>
  new Array(count).fill(null).map((_, i) => (
    /* eslint-disable react/no-array-index-key */
    <Skeleton
      key={i}
      containerClassName="photo-gallery__photo-button"
      className="photo-gallery__photo"
    />
    /* eslint-enable react/no-array-index-key */
  ));

const SCROLL_PERCENT_TO_LOAD = 0.5;

const PhotoGalleryTile = ({i, image, url, viewPhoto, getThumbnailUrl, isHostModule}) => {
  const [isLoading, setIsLoading] = useState(true);
  const idToUse = isHostModule ? image.photo_id : image.id;
  const imgAlt = isHostModule ? '' : image.comment || `${image.post_type} #${i}`;

  return (
    <Button
      variant="transparent"
      className="photo-gallery__photo-button"
      data-qa-id="photo-gallery-photo-button"
      onClick={viewPhoto(idToUse)}
      title="Click to display photo"
    >
      <img
        className="photo-gallery__photo"
        data-qa-id="photo-tile"
        src={getThumbnailUrl(url)}
        alt={imgAlt}
        onLoad={() => setIsLoading(false)}
      />
      {isLoading && (
        <Skeleton
          containerClassName="photo-gallery__skeleton-wrapper"
          className="photo-gallery__skeleton"
        />
      )}
    </Button>
  );
};

export const PhotoGallery = ({
  isOpen,
  onClose,
  isHostModule = false,
  galleryName = null,
  onEditGallery,
  handlePost,
  fileUploadRef,
}) => {
  const isMediumAndUp = useIsMediumAndUp();
  const isLargishAndUp = useIsLargishAndUp();
  const {getThumbnailUrl} = useImageProxySizer();
  const listRef = useRef(null);
  const eventId = useSelector((state) => state.eventDetails?.eventId);
  const [nextCursor, setNextCursor] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [photoViewerId, setPhotoViewerId] = useState(null);

  const {
    data: {objects: photos = [], more = false, cursor} = {objects: []},
    isFetching,
    isLoading,
  } = useAllPhotos({
    eventId,
    isHostModule,
    perPage: PHOTO_GALLERY_PAGE_CACHE_SIZE,
    includeCount: true,
    ...(nextCursor ? {cursor: nextCursor} : {}),
  });

  useEffect(() => {
    const onScroll = () => {
      const scrolledToBottom =
        listRef.current.scrollTop >=
        listRef.current.scrollHeight * SCROLL_PERCENT_TO_LOAD - listRef.current.offsetHeight;
      if (scrolledToBottom && !isFetching && !isLoading && more) {
        setNextCursor(cursor);
      }
    };

    if (listRef?.current) {
      listRef.current.addEventListener('scroll', onScroll);
      listRef.current.addEventListener('touchmove', onScroll);
    }
    const reference = listRef.current;
    return () => {
      if (reference) {
        reference.removeEventListener('scroll', onScroll);
        reference.removeEventListener('touchmove', onScroll);
      }
    };
  }, [cursor, more, isFetching, isLoading]);

  const viewPhoto = useCallback(
    (id) => () => {
      setPhotoViewerId(id);
      setPhotoViewerOpen(true);
    },
    []
  );

  const headerActions = useMemo(
    () =>
      isHostModule ? (
        <Button
          variant="secondary"
          data-qa-id="edit-photos-btn"
          onClick={onEditGallery}
          icon={<EditLineIcon ratio={0.5} />}
        >
          Edit
        </Button>
      ) : (
        <PhotoUploadButton
          variant="secondary"
          handlePost={handlePost}
          fileUploadRef={fileUploadRef}
          icon={<PlusIcon ratio={0.5} />}
        >
          Add
        </PhotoUploadButton>
      ),
    [isHostModule, onEditGallery, handlePost, fileUploadRef]
  );

  return (
    <>
      <AdaptiveModal
        primaryQaId="photo-gallery"
        id="photo-gallery-modal"
        headerText={galleryName ?? 'Shared photo album'}
        headerActions={(isHost() || !isHostModule) && photos.length ? headerActions : null}
        headerTextVariant="header3"
        onClose={onClose}
        isOpen={isOpen}
        headerClassName={cx('photo-gallery__header', {
          'photo-gallery__header--justify-end': isHostModule && !galleryName,
        })}
      >
        <div
          className={cx('photo-gallery__list', {
            'photo-gallery__list-host': isHostModule,
            'photo-gallery__list__not-empty': isLoading || photos.length > 0,
          })}
          ref={listRef}
          data-qa-id="photo-gallery-list"
        >
          {!isLoading && photos.length === 0 && (
            <EmptyState handlePost={handlePost} fileUploadRef={fileUploadRef} />
          )}
          {isLoading
            ? // eslint-disable-next-line no-nested-ternary
              loadingSkeletons(isLargishAndUp ? 9 : isMediumAndUp ? 6 : 4)
            : photos.map((image, i) => {
                const url = isHostModule ? image.url : image.content.url;
                return (
                  <PhotoGalleryTile
                    key={url}
                    i={i}
                    image={image}
                    url={url}
                    viewPhoto={viewPhoto}
                    getThumbnailUrl={getThumbnailUrl}
                    isHostModule={isHostModule}
                  />
                );
              })}
          {isFetching && !isLoading && (
            <div className="photo-gallery__loading-more-spinner">
              <span className="evt-spinner inline" />
            </div>
          )}
        </div>
      </AdaptiveModal>
      {photoViewerOpen && (
        <PhotoViewer
          initialPhotoId={photoViewerId}
          onClose={() => setPhotoViewerOpen(false)}
          isHostModule={isHostModule}
        />
      )}
    </>
  );
};
